import { newsletterKindEnum, newsletters } from '@markku/db'
import { createInsertSchema, createSelectSchema } from 'drizzle-zod'
import { z } from 'zod'
import { audienceFilterSchema } from './audienceFilter'
import { phoneNumberSchema } from './common'
import type { ServicePresetKey } from './servicePreset'

// Automation template keys: the thank-you voucher mail sent after a
// household visit, and its expiry reminder.
export const THANK_YOU_VOUCHER_KEY = 'thank_you_voucher'
export const THANK_YOU_VOUCHER_REMINDER_KEY = 'thank_you_voucher_reminder'

// The discontinued technician recommendation: one template per service plus a
// shared reminder. Kept only so mails queued before the switch can still go
// out; remove with the huoltosuositus code in mid-October 2026.
export const SERVICE_RECOMMENDATION_REMINDER_KEY =
  'service_recommendation_reminder'
export const serviceRecommendationKey = (service: string) =>
  `service_recommendation:${service}`

// What the thank-you voucher is worth on, keyed by booking-widget service, in
// the order the mail and the landing page list them. A flat amount would be
// most of a 68 € nuohous but a rounding error on an IV cleaning.
export const THANK_YOU_VOUCHER_SERVICES = {
  ilp_huolto: 'Ilmalämpöpumpun syväpuhdistus',
  ilmanvaihto_v2: 'Ilmanvaihtokanavien puhdistus',
  sadevesikourut: 'Rännien tyhjennys',
  nuohous: 'Nuohous',
} as const satisfies Partial<Record<ServicePresetKey, string>>

export type ThankYouVoucherService = keyof typeof THANK_YOU_VOUCHER_SERVICES

export const THANK_YOU_VOUCHER_SERVICE_KEYS = Object.keys(
  THANK_YOU_VOUCHER_SERVICES,
) as ThankYouVoucherService[]

export const newsletterAutomationSettingsSchema = z.object({
  enabled: z.boolean(),
  delayDays: z.number().int().min(0),
  voucherValues: z.object({
    ilp_huolto: z.number().positive(),
    ilmanvaihto_v2: z.number().positive(),
    sadevesikourut: z.number().positive(),
    nuohous: z.number().positive(),
  }) satisfies z.ZodType<Record<ThankYouVoucherService, number>>,
  // The old recommendation templates' flat value. Kept so saving one in the
  // ERP doesn't strip it while its queued mails drain; remove mid-October 2026.
  voucherValue: z.number().positive().optional(),
  voucherValidDays: z.number().int().min(1),
  reminderDaysBefore: z.number().int().min(0),
  // Where the mail's button points, also in the reminder. The voucher code
  // and UTM tags are appended at send time. Optional in the shape (the
  // reminder template has none), required to enable the voucher template.
  bookingUrl: z.string().url().optional(),
})

export type NewsletterAutomationSettings = z.infer<
  typeof newsletterAutomationSettingsSchema
>

export const DEFAULT_AUTOMATION_SETTINGS: NewsletterAutomationSettings = {
  enabled: false,
  delayDays: 2,
  voucherValues: {
    ilp_huolto: 50,
    ilmanvaihto_v2: 50,
    sadevesikourut: 25,
    nuohous: 8,
  },
  voucherValidDays: 14,
  reminderDaysBefore: 2,
}

export const selectNewsletterSchema = createSelectSchema(newsletters, {
  // Internal-prefixed field: optional so it validates when stripped for
  // non-privileged users (see CLAUDE.md internal-field convention).
  internalName: z.string().nullable().optional(),
})
export const insertNewsletterSchema = createInsertSchema(newsletters).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
})

// Back-compat alias: newsletters referenced `newsletterAudienceFilterSchema`
// before the shared audience module was extracted.
export const newsletterAudienceFilterSchema = audienceFilterSchema

export const upsertNewsletterSchema = z.object({
  subject: z.string().min(1),
  internalName: z.string().optional(),
  designJson: z.any().optional(),
  html: z.string().optional(),
  audienceFilter: audienceFilterSchema.optional(),
  senderUserId: z.number().nullable().optional(),
  kind: z.enum(newsletterKindEnum.enumValues).optional(),
  automationKey: z.string().nullable().optional(),
  automationSettings: newsletterAutomationSettingsSchema.optional(),
})

// Profile behind the {{sender…}} merge tags; name, email and phone come from
// the user record itself.
export const senderProfileSchema = z.object({
  jobTitle: z.string().nullable(),
  // Key required like the siblings; the pipe normalises to +358 form
  publicPhoneNumber: z.string().nullable().pipe(phoneNumberSchema),
  linkedinUrl: z.string().url().nullable(),
  photoUrl: z.string().url().nullable(),
  // Optional so an ERP tab loaded before the field existed can still save;
  // the upsert then leaves the stored value untouched.
  useOwnEmail: z.boolean().optional(),
  bio: z.string().max(300).nullable().optional(),
})

export type Newsletter = z.infer<typeof selectNewsletterSchema>
export type SenderProfile = z.infer<typeof senderProfileSchema>
export type InsertNewsletter = z.infer<typeof insertNewsletterSchema>
export type UpsertNewsletter = z.infer<typeof upsertNewsletterSchema>
export type NewsletterAudienceFilter = z.infer<typeof audienceFilterSchema>
